import React, { createContext, useContext, useState, useEffect } from "react";
import { BASE_URL } from "../config/apiConfig";
import { useAuth } from "./AuthContext";
import { toast } from "react-toastify";

const AssetTypeContext = createContext();

export const useAssetType = () => useContext(AssetTypeContext);

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
    try {
        const text = await res.text();
        if (!text) {
            return { status: res.status, message: "Empty response from server" };
        }
        try {
            return JSON.parse(text);
        } catch (parseErr) {
            return { status: res.status, message: "Invalid response format from server" };
        }
    } catch (readErr) {
        return { status: res.status || 500, message: "Failed to read server response" };
    }
};

export const AssetTypeProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();

    const [assetTypes, setAssetTypes] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState(null);

    // =========================
    // FETCH ALL ASSET TYPES — fetch operation, no toasts
    // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
    // =========================
    const fetchAssetTypes = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/asset-types`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                setAssetTypes(data.data || []);
                return { success: true, data: data.data };
            } else {
                setError(data.message || "Failed to fetch asset types");
                // CHANGED: removed toast.error(data.message || "Failed to fetch asset types")
                return { success: false, message: data.message };
            }
        } catch (err) {
            console.error("Fetch asset types error:", err);
            setError("Network error");
            // CHANGED: removed toast.error("Network error")
            return { success: false, message: "Network error" };
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // CREATE ASSET TYPE — user action, toasts preserved
    // =========================
    const createAssetType = async (payload) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/asset-types`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 201) {
                await fetchAssetTypes();
                toast.success(data.message || "Asset type created successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to create asset type");
                toast.error(data.message || "Failed to create asset type"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            setError("Network error");
            toast.error("Network error"); // KEPT: user-initiated action
            return { success: false, message: "Network error" };
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // UPDATE ASSET TYPE — user action, toasts preserved
    // =========================
    const updateAssetType = async (id, payload) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/asset-types/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                await fetchAssetTypes();
                toast.success(data.message || "Asset type updated successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to update asset type");
                toast.error(data.message || "Failed to update asset type"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            setError("Network error");
            toast.error("Network error"); // KEPT: user-initiated action
            return { success: false, message: "Network error" };
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // UPDATE STATUS ONLY — user action, toasts preserved
    // =========================
    const updateAssetTypeStatus = async (id, payload) => {
        setStatusLoading(true);
        setStatusError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/asset-types/${id}/status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                await fetchAssetTypes();
                toast.success(data.message || "Status updated successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setStatusError(data.message || "Failed to update status");
                toast.error(data.message || "Failed to update status"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            setStatusError("Network error");
            toast.error("Network error"); // KEPT: user-initiated action
            return { success: false, message: "Network error" };
        } finally {
            setStatusLoading(false);
        }
    };

    // =========================
    // DELETE (soft delete) — user action, toasts preserved
    // =========================
    const deleteAssetType = async (id, delete_audit_id) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/asset-types/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ delete_audit_id }),
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                await fetchAssetTypes();
                toast.success(data.message || "Asset type deleted successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to delete asset type");
                toast.error(data.message || "Failed to delete asset type"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            setError("Network error");
            toast.error("Network error"); // KEPT: user-initiated action
            return { success: false, message: "Network error" };
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // AUTO LOAD
    // =========================
    useEffect(() => {
        if (isAuthenticated) {
            fetchAssetTypes();
        }
    }, [isAuthenticated]);

    return (
        <AssetTypeContext.Provider
            value={{
                assetTypes,
                loading,
                error,

                statusLoading,
                statusError,

                fetchAssetTypes,
                createAssetType,
                updateAssetType,
                updateAssetTypeStatus,
                deleteAssetType,
                setAssetTypes,
            }}
        >
            {children}
        </AssetTypeContext.Provider>
    );
};